import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindValue, html } from "../lib/bind";
import { form } from "../lib/form";
import { brandLockup } from "../lib/logo";
import { addPasswordToggle } from "../lib/passwordField";
import { fetchMe, login, verifyTotp } from "../services/auth";

export function loginView(): Node {
  const stage = signal<"password" | "totp">("password");
  const errorMessage = signal("");
  const preauthToken = signal("");
  const submitting = signal(false);

  const passwordForm = form({ email: "", password: "" });
  const codeForm = form({ code: "" });

  const isPasswordStage = computed(() => stage.value === "password");
  const isTotpStage = computed(() => stage.value === "totp");

  async function submitPassword(event: Event): Promise<void> {
    event.preventDefault();
    if (submitting.value) return;
    errorMessage.value = "";
    submitting.value = true;
    try {
      const result = await login(
        passwordForm.fields.email.value,
        passwordForm.fields.password.value,
      );
      if (result.totpRequired && result.preauthToken) {
        preauthToken.value = result.preauthToken;
        stage.value = "totp";
        codeInput?.focus();
      } else {
        await fetchMe();
        location.assign("/");
      }
    } catch (err) {
      errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Login failed";
    } finally {
      submitting.value = false;
    }
  }

  async function submitCode(event: Event): Promise<void> {
    event.preventDefault();
    if (submitting.value) return;
    errorMessage.value = "";
    submitting.value = true;
    try {
      await verifyTotp(preauthToken.value, codeForm.fields.code.value);
      await fetchMe();
      location.assign("/");
    } catch (err) {
      errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Invalid code";
    } finally {
      submitting.value = false;
    }
  }

  const fragment = html`<div class="auth-page">
    <div class="auth-card">
      <div class="auth-brand">${brandLockup("mark", "brand-lg")}</div>
      <form class="form auth-form-password" onsubmit="${submitPassword}">
        <div class="form-field">
          <label for="login-email">Email</label>
          <input id="login-email" type="email" autocomplete="username" required />
        </div>
        <div class="form-field">
          <label for="login-password">Password</label>
          <input id="login-password" type="password" autocomplete="current-password" required />
        </div>
        <p class="form-error"></p>
        <button class="btn btn-primary" type="submit">Log in</button>
      </form>
      <form class="form auth-form-totp" onsubmit="${submitCode}">
        <div class="form-field">
          <label for="login-code">Authenticator code</label>
          <input id="login-code" autocomplete="one-time-code" inputmode="numeric" required />
        </div>
        <p class="form-error"></p>
        <button class="btn btn-primary" type="submit">Verify</button>
      </form>
    </div>
    <p class="auth-footer">Powered by piofthings.ai</p>
  </div>`;

  const passwordFormEl = fragment.querySelector<HTMLFormElement>(".auth-form-password");
  const totpFormEl = fragment.querySelector<HTMLFormElement>(".auth-form-totp");
  const emailInput = fragment.querySelector<HTMLInputElement>("#login-email");
  const passwordInput = fragment.querySelector<HTMLInputElement>("#login-password");
  const codeInput = fragment.querySelector<HTMLInputElement>("#login-code");
  const errorEls = fragment.querySelectorAll<HTMLParagraphElement>(".form-error");

  if (!passwordFormEl || !totpFormEl || !emailInput || !passwordInput || !codeInput) {
    throw new Error("login view markup missing expected elements");
  }

  bindValue(emailInput, passwordForm.fields.email);
  bindValue(passwordInput, passwordForm.fields.password);
  bindValue(codeInput, codeForm.fields.code);
  addPasswordToggle(passwordInput);

  bindShow(passwordFormEl, isPasswordStage);
  bindShow(totpFormEl, isTotpStage);

  for (const errorEl of errorEls) bindErrorText(errorEl, errorMessage);

  return fragment;
}
